import React, { useState } from 'react';
import {
  Box,
  Typography,
  IconButton,
  Button,
  Paper,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  TextField,
  Chip,
  Stack,
  Autocomplete,
  CircularProgress,
  MenuItem,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonIcon from '@mui/icons-material/Person';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { permissionService } from '../services/permissionService';
import { authService } from '../services/authService';

const isDateFieldKey = (key: string): boolean => {
  const normalizedKey = key.toLowerCase();
  return normalizedKey.includes('datum')
    || normalizedKey === 'projektkezdete'
    || normalizedKey === 'tervezettbefejezes';
};

interface DetailPanelProps {
  isOpen: boolean;
  data: any;
  columns: string[];
  onClose: () => void;
  onEdit?: (updatedData: any) => void;
  onDelete?: () => void;
  formatColumnName: (key: string) => string;
  formatCellValue: (key: string, value: any) => string;
  readonlyFields?: string[];
  editPermission?: string;
  deletePermission?: string;
  dataType?: string;
  onNavigateToParticipantTimeTracking?: (participantName: string, munkaszam: string) => void;
  onNavigateToFolyamatlap?: (munkaszam: string) => void;
  onGetFieldSuggestions?: (field: string, query: string, dataType?: string) => Promise<string[]>;
}

const DetailPanel: React.FC<DetailPanelProps> = ({
  isOpen,
  data,
  columns,
  onClose,
  onEdit,
  onDelete,
  formatColumnName,
  formatCellValue,
  readonlyFields = [],
  editPermission,
  deletePermission,
  dataType,
  onNavigateToParticipantTimeTracking,
  onNavigateToFolyamatlap,
  onGetFieldSuggestions,
}) => {
  const canEdit = editPermission ? permissionService.hasPermission(editPermission) : true;
  const canDelete = deletePermission ? permissionService.hasPermission(deletePermission) : true;
  const canViewParticipants =
    permissionService.hasPermission('Permissions.ViewWorkNumberParticipants') &&
    authService.hasRole('Adminisztrátor');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedData, setEditedData] = useState<any>({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [fieldSuggestions, setFieldSuggestions] = useState<Record<string, string[]>>({});
  const [fieldLoading, setFieldLoading] = useState<Record<string, boolean>>({});
  const [fieldInputValues, setFieldInputValues] = useState<Record<string, string>>({});

  React.useEffect(() => {
    if (data) {
      setEditedData({ ...data });

      const initialInputValues: Record<string, string> = {};
      Object.keys(data).forEach((key) => {
        if (key === 'resztvevok') {
          initialInputValues[key] = '';
        } else {
          initialInputValues[key] = data[key] == null ? '' : String(data[key]);
        }
      });
      setFieldInputValues(initialInputValues);
      setFieldSuggestions({});
      setFieldLoading({});
    }
  }, [data]);

  const isAutocompleteField = (key: string): boolean => {
    if (!dataType) return false;

    const mapping: Record<string, string[]> = {
      ajanlatok: ['megrendelo', 'ugyintezo'],
      iktatas: ['munkaszam', 'cimzettKuldo', 'ugyintezo'],
      munkaszamok: ['megrendelo', 'ugyintezo', 'resztvevok'],
    };

    return (mapping[dataType] || []).indexOf(key) !== -1;
  };

  const isSelectField = (key: string): boolean => {
    return dataType === 'ajanlatok' && key === 'valasz';
  };

  const handleAutocompleteInputChange = async (key: string, inputValue: string) => {
    setFieldInputValues((prev) => ({
      ...prev,
      [key]: inputValue,
    }));

    if (key !== 'resztvevok') {
      handleFieldChange(key, inputValue);
    }

    const queryValue = key === 'resztvevok'
      ? inputValue.split(/[,;]/).pop()?.trim() ?? ''
      : inputValue.trim();

    if (!onGetFieldSuggestions || queryValue.length < 2) {
      setFieldSuggestions((prev) => ({
        ...prev,
        [key]: [],
      }));
      return;
    }

    setFieldLoading((prev) => ({
      ...prev,
      [key]: true,
    }));

    try {
      const suggestions = await onGetFieldSuggestions(key, queryValue, dataType);
      setFieldSuggestions((prev) => ({
        ...prev,
        [key]: suggestions,
      }));
    } catch (error) {
      console.error('Autocomplete javaslat betöltési hiba:', error);
      setFieldSuggestions((prev) => ({
        ...prev,
        [key]: [],
      }));
    } finally {
      setFieldLoading((prev) => ({
        ...prev,
        [key]: false,
      }));
    }
  };

  const handleEditClick = () => {
    setIsEditMode(true);
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedData({ ...data });
  };

  const handleSaveEdit = () => {
    if (onEdit) {
      onEdit(editedData);
    }
    setIsEditMode(false);
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (onDelete) {
      onDelete();
    }
    setDeleteDialogOpen(false);
    onClose();
  };

  const handleFieldChange = (key: string, value: any) => {
    let processedValue = value;
    
    // Dátumnál kerüljük az UTC konverziót, mert napelcsúszást okozhat.
    if (isDateFieldKey(key)) {
      if (!value) {
        processedValue = null;
      } else {
        try {
          if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
            processedValue = `${value}T12:00:00`;
          } else {
            const date = new Date(value);
            if (!isNaN(date.getTime())) {
              const year = date.getFullYear();
              const monthNum = date.getMonth() + 1;
              const dayNum = date.getDate();
              const month = monthNum < 10 ? `0${monthNum}` : `${monthNum}`;
              const day = dayNum < 10 ? `0${dayNum}` : `${dayNum}`;
              processedValue = `${year}-${month}-${day}T12:00:00`;
            }
          }
        } catch (e) {
          console.error('Hiba a dátum konvertálása során:', e);
        }
      }
    }
    
    setEditedData((prev: any) => ({
      ...prev,
      [key]: processedValue,
    }));
  };

  const parseParticipants = (rawValue: any): string[] => {
    if (Array.isArray(rawValue)) {
      return rawValue
        .map((item) => String(item).trim())
        .filter((item) => item.length > 0);
    }

    return String(rawValue ?? '')
      .split(/[,;]/)
      .map((item) => item.trim())
      .filter((item) => item.length > 0);
  };

  const normalizeParticipants = (values: any[]): string[] => {
    const seen = new Set<string>();
    const normalized: string[] = [];

    values
      .map((value) => String(value).trim())
      .filter((value) => value.length > 0)
      .forEach((value) => {
        const key = value.toLowerCase();
        if (!seen.has(key)) {
          seen.add(key);
          normalized.push(value);
        }
      });

    return normalized;
  };

  const handleParticipantClick = (participantName: string) => {
    if (canViewParticipants && onNavigateToParticipantTimeTracking && data?.munkaszam) {
      onNavigateToParticipantTimeTracking(participantName.trim(), data.munkaszam);
    }
  };

  const renderParticipantsField = (value: string) => {
    if (!value || value === '-') {
      return <Typography variant="body1" sx={{ mt: 0.5, color: 'text.secondary' }}>-</Typography>;
    }

    // Résztvevők feldarabolása vesszővel vagy pontosvesszővel
    const participants = value.split(/[,;]/).map(p => p.trim()).filter(p => p.length > 0);
    
    if (participants.length === 0) {
      return <Typography variant="body1" sx={{ mt: 0.5, color: 'text.secondary' }}>-</Typography>;
    }

    return (
      <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mt: 0.5, gap: 1 }}>
        {participants.map((participant, index) => (
          <Chip
            key={index}
            label={participant}
            icon={<PersonIcon />}
            onClick={() => handleParticipantClick(participant)}
            clickable={canViewParticipants && !!onNavigateToParticipantTimeTracking}
            color="primary"
            variant="outlined"
            sx={{
              cursor: canViewParticipants && onNavigateToParticipantTimeTracking ? 'pointer' : 'default',
              '&:hover': canViewParticipants && onNavigateToParticipantTimeTracking ? {
                backgroundColor: 'primary.light',
                color: 'white',
              } : {},
            }}
          />
        ))}
      </Stack>
    );
  };

  if (!data) return null;

  return (
    <>
      {/* Detail Panel */}
      <Paper
        elevation={8}
        sx={{
          position: 'fixed',
          top: 0,
          right: 0,
          bottom: 0,
          width: '520px',
          zIndex: 1300,
          display: 'flex',
          flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.5s ease-in-out',
          backgroundColor: 'white',
          overflowY: 'auto',
          boxShadow: '-4px 0 8px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            backgroundColor: '#f5f5f5',
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Részletek
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flexGrow: 1, p: 3, overflowY: 'auto' }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {columns.map((key, index) => {
              const value = isEditMode ? editedData[key] : data[key];
              const isDateField = isDateFieldKey(key);
              const isReadonly =
                readonlyFields.indexOf(key) !== -1
                || (dataType === 'munkaszamok' && key === 'munkaszam');
              
              // Check if this is a participants or manager field in munkaszam view
              const isParticipantsField = dataType === 'munkaszamok' && (key === 'resztvevok' || key === 'ugyintezo');
              
              // Convert date to YYYY-MM-DD format for input[type="date"]
              let dateValue = value;
              if (isEditMode && isDateField && value) {
                try {
                  const date = new Date(value);
                  if (!isNaN(date.getTime())) {
                    const year = date.getFullYear();
                    const monthNum = date.getMonth() + 1;
                    const dayNum = date.getDate();
                    const month = monthNum < 10 ? `0${monthNum}` : `${monthNum}`;
                    const day = dayNum < 10 ? `0${dayNum}` : `${dayNum}`;
                    dateValue = `${year}-${month}-${day}`;
                  }
                } catch (e) {
                  // Keep original value if parsing fails
                }
              }
              
              return (
                <Box key={key}>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 600,
                        color: 'text.secondary',
                        textTransform: 'uppercase',
                        fontSize: '0.75rem',
                      }}
                    >
                      {formatColumnName(key)}
                    </Typography>
                    
                    {isEditMode ? (
                      isReadonly ? (
                        <Typography
                          variant="body1"
                          sx={{
                            mt: 0.5,
                            color: 'text.primary',
                            wordBreak: 'break-word',
                          }}
                        >
                          {formatCellValue(key, value)}
                        </Typography>
                      ) : isDateField ? (
                        <TextField
                          fullWidth
                          size="small"
                          value={dateValue || ''}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          sx={{ mt: 0.5 }}
                        />
                      ) : isSelectField(key) ? (
                        <TextField
                          fullWidth
                          size="small"
                          select
                          value={value ?? ''}
                          onChange={(e) => handleFieldChange(key, e.target.value === '' ? null : e.target.value)}
                          sx={{ mt: 0.5 }}
                        >
                          <MenuItem value="">Nincs megadva</MenuItem>
                          <MenuItem value="Elfogadva">Elfogadva</MenuItem>
                          <MenuItem value="Elutasítva">Elutasítva</MenuItem>
                        </TextField>
                      ) : isAutocompleteField(key) ? (
                        key === 'resztvevok' ? (
                        <Autocomplete
                          multiple
                          freeSolo
                          options={fieldSuggestions[key] || []}
                          filterOptions={(options) => options}
                          getOptionLabel={(option) => String(option ?? '')}
                          value={parseParticipants(value)}
                          inputValue={fieldInputValues[key] ?? ''}
                          onChange={(_, newValue) => {
                            const participantList = normalizeParticipants(newValue as any[]);
                            const joinedValue = participantList.join('; ');

                            setFieldInputValues((prev) => ({
                              ...prev,
                              [key]: '',
                            }));

                            handleFieldChange(key, joinedValue);
                          }}
                          onInputChange={(_, newInputValue) => {
                            if (!isReadonly) {
                              handleAutocompleteInputChange(key, newInputValue);
                            }
                          }}
                          loading={!!fieldLoading[key]}
                          disabled={isReadonly}
                          renderTags={(tagValue: readonly string[], getTagProps) =>
                            tagValue.map((option: string, index: number) => {
                              const { key: chipKey, ...chipProps } = getTagProps({ index });
                              return (
                                <Chip
                                  key={chipKey}
                                  variant="outlined"
                                  label={option}
                                  size="small"
                                  {...chipProps}
                                />
                              );
                            })
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              size="small"
                              sx={{ mt: 0.5 }}
                              placeholder="Név hozzáadása..."
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {fieldLoading[key] ? <CircularProgress color="inherit" size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          noOptionsText="Nincs találat"
                        />
                      ) : (
                        <Autocomplete
                          freeSolo
                          options={fieldSuggestions[key] || []}
                          filterOptions={(options) => options}
                          getOptionLabel={(option) => String(option ?? '')}
                          value={value || ''}
                          inputValue={fieldInputValues[key] ?? (value || '')}
                          onChange={(_, newValue) => {
                            const selectedValue = (newValue || '').toString().trim();

                            if (key === 'resztvevok') {
                              const rawInput = (fieldInputValues[key] ?? (value || '')).toString();
                              const hasActiveToken = !/[;,]\s*$/.test(rawInput);
                              const parts = rawInput
                                .split(/[,;]/)
                                .map((part) => part.trim())
                                .filter((part) => part.length > 0);

                              const committedParts = hasActiveToken && parts.length > 0
                                ? parts.slice(0, -1)
                                : parts;

                              const exists = committedParts.some(
                                (participant) => participant.toLowerCase() === selectedValue.toLowerCase()
                              );

                              const updatedParticipants = selectedValue && !exists
                                ? [...committedParts, selectedValue]
                                : committedParts;

                              const joinedValue = updatedParticipants.join('; ');

                              setFieldInputValues((prev) => ({
                                ...prev,
                                [key]: joinedValue,
                              }));

                              handleFieldChange(key, joinedValue);
                              return;
                            }

                            setFieldInputValues((prev) => ({
                              ...prev,
                              [key]: selectedValue,
                            }));
                            handleFieldChange(key, selectedValue);
                          }}
                          onInputChange={(_, newInputValue) => {
                            if (!isReadonly) {
                              handleAutocompleteInputChange(key, newInputValue);
                            }
                          }}
                          loading={!!fieldLoading[key]}
                          disabled={isReadonly}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              fullWidth
                              size="small"
                              sx={{ mt: 0.5 }}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {fieldLoading[key] ? <CircularProgress color="inherit" size={16} /> : null}
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          noOptionsText="Nincs találat"
                        />
                      )
                      ) : (
                        <TextField
                          fullWidth
                          size="small"
                          value={value || ''}
                          onChange={(e) => handleFieldChange(key, e.target.value)}
                          type="text"
                          sx={{ mt: 0.5 }}
                        />
                      )
                    ) : isParticipantsField ? (
                      renderParticipantsField(value)
                    ) : (
                      <Typography
                        variant="body1"
                        sx={{
                          mt: 0.5,
                          color: 'text.primary',
                          wordBreak: 'break-word',
                        }}
                      >
                        {formatCellValue(key, value)}
                      </Typography>
                    )}
                  </Box>
                  {index !== columns.length - 1 && (
                    <Divider sx={{ mt: 2 }} />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Footer Actions */}
        <Box
          sx={{
            p: 2,
            borderTop: '1px solid #e0e0e0',
            backgroundColor: '#f5f5f5',
            display: 'flex',
            gap: 1,
            justifyContent: 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          {isEditMode ? (
            <>
              <Button
                variant="outlined"
                onClick={handleCancelEdit}
                size="medium"
              >
                Mégse
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveEdit}
                startIcon={<EditIcon />}
                size="medium"
              >
                Mentés
              </Button>
            </>
          ) : (
            <>
              {dataType === 'munkaszamok' && data?.munkaszam && onNavigateToFolyamatlap && (
                <Button
                  variant="outlined"
                  onClick={() => onNavigateToFolyamatlap(data.munkaszam)}
                  startIcon={<AssignmentIcon />}
                  size="medium"
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  Folyamatlap generálása
                </Button>
              )}
              {canEdit && onEdit && (
                <Button
                  variant="contained"
                  onClick={handleEditClick}
                  startIcon={<EditIcon />}
                  size="medium"
                >
                  Szerkesztés
                </Button>
              )}
              {canDelete && onDelete && (
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleDeleteClick}
                  startIcon={<DeleteIcon />}
                  size="medium"
                >
                  Törlés
                </Button>
              )}
            </>
          )}
        </Box>
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Törlés megerősítése</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Biztosan törölni szeretnéd ezt az elemet? Ez a művelet nem vonható vissza.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>
            Mégse
          </Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Törlés
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DetailPanel;
